import { createContext, useContext, useState, useEffect } from 'react'
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core'

export const VISUAL_EFFECTS_STORAGE_KEY = 'suguide_visual_effects_enabled'
export const VISUAL_EFFECTS_CHANGED_EVENT = 'suguide:visual-effects-changed'
export const CAMPUS_OS_MODE_KEY = 'su-guide-campus-os-mode'
const MAX_MODE_KEY = 'suguide_max_mode_enabled'

export function readVisualEffectsEnabled() {
  try {
    const stored = localStorage.getItem(VISUAL_EFFECTS_STORAGE_KEY)
    if (stored !== null) return stored === 'true'
    // migrate from visual engine key
    const engine = localStorage.getItem('suguide_visual_engine')
    if (engine !== null) return engine !== 'clean'
    return true
  } catch { return true }
}

const ThemeContext = createContext({
  isDark: false, toggle: () => {},
  visualEffects: true, toggleVisualEffects: () => {},
  maxModeEnabled: false, toggleMaxMode: () => {}, setMaxModeEnabled: () => {},
  campusOsMode: false, toggleCampusOsMode: () => {}, setCampusOsMode: () => {},
})

export function ThemeProvider({ children }) {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem('su-guide-theme') === 'dark' } catch { return false }
  })

  const [visualEffects, setVisualEffects] = useState(() => readVisualEffectsEnabled())

  const [maxModeEnabled, setMaxModeEnabled] = useState(() => {
    try { return localStorage.getItem(MAX_MODE_KEY) === 'true' } catch { return false }
  })

  const [campusOsMode, setCampusOsMode] = useState(() => {
    try { return localStorage.getItem(CAMPUS_OS_MODE_KEY) === 'true' } catch { return false }
  })

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)
    try { localStorage.setItem('su-guide-theme', isDark ? 'dark' : 'light') } catch {}
  }, [isDark])

  // Android 상태바 아이콘 명암. SystemBars DEFAULT는 기기 다크 모드를 따르지만 앱 테마는 기기와 별개다.
  // 웹 화면이 실제로 상태바 뒤까지 그려질 때(SystemBars가 --safe-area-inset-top > 0을 주입 = Android 15+ edge-to-edge)만
  // 앱 테마를 따른다. 그 외(API ≤34 등)에는 상태바 배경이 시스템 테마 색이므로 DEFAULT가 맞다.
  // iOS는 WKWebView가 페이지 색으로 명암을 정하므로 건드리지 않는다.
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return
    const root = document.documentElement
    let applied = null
    const apply = () => {
      const underStatusBar = parseFloat(root.style.getPropertyValue('--safe-area-inset-top')) > 0
      const style = !underStatusBar ? SystemBarsStyle.Default : isDark ? SystemBarsStyle.Dark : SystemBarsStyle.Light
      if (style === applied) return
      applied = style
      SystemBars.setStyle({ style }).catch(() => {})
    }
    apply()
    // 인셋은 DOM 준비·회전·제스처/3버튼 전환 때 비동기로 다시 주입된다.
    const observer = new MutationObserver(apply)
    observer.observe(root, { attributes: true, attributeFilter: ['style'] })
    return () => observer.disconnect()
  }, [isDark])

  useEffect(() => {
    document.documentElement.classList.toggle('effects-on', visualEffects)
    document.documentElement.classList.toggle('effects-off', !visualEffects)
    try { localStorage.setItem(VISUAL_EFFECTS_STORAGE_KEY, String(visualEffects)) } catch {}
    try { window.dispatchEvent(new Event(VISUAL_EFFECTS_CHANGED_EVENT)) } catch {}
  }, [visualEffects])

  useEffect(() => {
    try { localStorage.setItem(MAX_MODE_KEY, String(maxModeEnabled)) } catch {}
  }, [maxModeEnabled])

  useEffect(() => {
    try { localStorage.setItem(CAMPUS_OS_MODE_KEY, String(campusOsMode)) } catch {}
  }, [campusOsMode])

  function toggleVisualEffects() {
    setVisualEffects(v => !v)
  }

  function toggleMaxMode() {
    setMaxModeEnabled(v => !v)
  }

  function toggleCampusOsMode() {
    setCampusOsMode(v => !v)
  }

  return (
    <ThemeContext.Provider value={{
      isDark,
      toggle: () => setIsDark(v => !v),
      visualEffects,
      toggleVisualEffects,
      maxModeEnabled,
      toggleMaxMode,
      setMaxModeEnabled,
      campusOsMode,
      toggleCampusOsMode,
      setCampusOsMode,
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  return useContext(ThemeContext)
}
